"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useDaumPostcodePopup } from "react-daum-postcode";
import AddressSearchEmbed from "./AddressSearchEmbed";
import { getCoordinates } from "@/lib/geocode";
import { Input } from "@/components/ui/input";
import type { DaumPostcodeData, AddressInfo } from "@/types/address";

const POSTCODE_SCRIPT_URL = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";

interface Props {
  label: string;
  color: string;
  value: AddressInfo | null;
  onChange: (info: AddressInfo) => void;
}

export default function Step2AddressInput({ label, color, value, onChange }: Props) {
  const [showEmbed, setShowEmbed] = useState(false);
  const [isGeoLoading, setIsGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [localDetail, setLocalDetail] = useState(value?.addressDetail || "");
  const composingRef = useRef(false);
  const detailRef = useRef<HTMLInputElement>(null);
  const openPostcode = useDaumPostcodePopup(POSTCODE_SCRIPT_URL);

  // 외부에서 value가 바뀌면(주소 재선택 등) 로컬 상태도 동기화
  useEffect(() => {
    if (!composingRef.current) {
      setLocalDetail(value?.addressDetail || "");
    }
  }, [value?.addressDetail]);

  const openPopup = useCallback(() => {
    openPostcode({
      onComplete: (data) => {
        let fullAddress = data.address;
        let extraAddress = "";
        if (data.addressType === "R") {
          if (data.bname) extraAddress += data.bname;
          if (data.buildingName) {
            extraAddress += extraAddress ? `, ${data.buildingName}` : data.buildingName;
          }
          if (extraAddress) fullAddress += ` (${extraAddress})`;
        }
        handleAddressSelect({ ...data, address: fullAddress } as DaumPostcodeData);
      },
    });
  }, [openPostcode]);

  useEffect(() => {
    setIsMobile(window.innerWidth < 640);
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleAddressSelect = async (data: DaumPostcodeData) => {
    setShowEmbed(false);
    setGeoError("");

    // 1) 주소 정보 즉시 반영
    const newInfo: AddressInfo = {
      address: data.address,
      addressDetail: "",
      zonecode: data.zonecode,
      roadAddress: data.roadAddress,
      jibunAddress: data.jibunAddress,
      buildingName: data.buildingName,
      hasElevator: true,
      floor: 1,
    };
    onChange(newInfo);

    // 2) 좌표 변환 (비동기)
    setIsGeoLoading(true);
    const coords = await getCoordinates(data.roadAddress || data.address);
    setIsGeoLoading(false);

    if (coords) {
      onChange({ ...newInfo, lat: coords.lat, lng: coords.lng });
    } else {
      setGeoError("좌표를 가져오지 못했습니다. 주소를 다시 검색해주세요.");
    }

    // 3) 상세주소 입력란으로 포커스
    setTimeout(() => detailRef.current?.focus(), 100);
  };

  const handleDetailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalDetail(val);
    if (!composingRef.current && value) {
      onChange({ ...value, addressDetail: val });
    }
  };

  const handleCompositionStart = () => {
    composingRef.current = true;
  };

  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    composingRef.current = false;
    if (value) {
      onChange({ ...value, addressDetail: (e.target as HTMLInputElement).value });
    }
  };

  return (
    <div className="space-y-3">
      {/* 라벨 */}
      <label className="text-sm font-bold" style={{ color }}>
        {label}
      </label>

      {/* 주소 검색 버튼 + 결과 */}
      <div className="flex gap-2">
        <Input
          type="text"
          readOnly
          placeholder="주소를 검색해주세요"
          value={value?.address || ""}
          className="flex-1 bg-gray-50 cursor-pointer"
          onClick={() => (isMobile ? setShowEmbed(true) : openPopup())}
        />

        {/* PC: 팝업 방식 */}
        {!isMobile && (
          <button
            type="button"
            onClick={openPopup}
            className="px-4 py-2 text-white text-sm font-semibold rounded-lg
                       whitespace-nowrap hover:opacity-90 transition-opacity"
            style={{ backgroundColor: color }}
          >
            검색
          </button>
        )}

        {/* 모바일: 버튼 클릭 시 임베드 */}
        {isMobile && (
          <button
            type="button"
            onClick={() => setShowEmbed(true)}
            className="px-4 py-2 text-white text-sm font-semibold rounded-lg whitespace-nowrap"
            style={{ backgroundColor: color }}
          >
            검색
          </button>
        )}
      </div>

      {/* 모바일 임베드 모달 */}
      {showEmbed && (
        <AddressSearchEmbed
          onComplete={handleAddressSelect}
          onClose={() => setShowEmbed(false)}
        />
      )}

      {/* 좌표 변환 상태 */}
      {isGeoLoading && (
        <p className="text-xs text-blue-500 flex items-center gap-1">
          <span className="inline-block w-3 h-3 border-2 border-blue-500
                           border-t-transparent rounded-full animate-spin" />
          좌표를 확인하고 있습니다...
        </p>
      )}
      {geoError && (
        <p className="text-xs text-red-500">{geoError}</p>
      )}

      {/* 상세주소 입력 (주소 선택 후 나타남) */}
      {value?.address && (
        <Input
          ref={detailRef}
          type="text"
          placeholder="상세주소 입력 (동/호수)"
          value={localDetail}
          onChange={handleDetailChange}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          className="w-full bg-white"
        />
      )}

      {/* 우편번호 + 좌표 상태 표시 */}
      {value?.zonecode && (
        <p className="text-xs text-gray-400">
          우편번호: {value.zonecode}
          {value.lat && value.lng && (
            <span className="ml-2 text-green-600">
              ✓ 좌표 확인됨
            </span>
          )}
        </p>
      )}
    </div>
  );
}
